// Vendor payment canonicalization and response classification. Durable, company/
// Bill-scoped attempts and exact-body posting live in vendorPaymentPersistence.ts.


export type VendorPaymentPayload = {
  bill_id: number;
  vendor_payment_date: string;
  amount: string;
  payment_account_id: number;
};

export function canonicalVendorPayment(
  billId: string | number,
  paymentDate: string,
  amount: string | number,
  paymentAccountId: string | number,
): VendorPaymentPayload {
  return {
    bill_id: Number(billId),
    vendor_payment_date: paymentDate,
    amount: Number(amount).toFixed(2),
    payment_account_id: Number(paymentAccountId),
  };
}

export function fingerprintVendorPayment(payload: VendorPaymentPayload): string {
  return JSON.stringify(payload);
}

export function randomUuidKey(): string {
  return crypto.randomUUID();
}

export type VendorPaymentAttemptState = "idle" | "in-flight" | "ambiguous";

export type VendorPaymentAttempt = {
  state: VendorPaymentAttemptState;
  billId: number | null;
  idempotencyKey: string | null;
  submittedFingerprint: string | null;
  submittedPayload: VendorPaymentPayload | null;
};

export const IDLE_VENDOR_PAYMENT_ATTEMPT: VendorPaymentAttempt = {
  state: "idle",
  billId: null,
  idempotencyKey: null,
  submittedFingerprint: null,
  submittedPayload: null,
};

export type VendorPaymentSubmitDecision =
  | { kind: "start"; idempotencyKey: string; attempt: VendorPaymentAttempt }
  | { kind: "blocked-mismatch" }
  | { kind: "blocked-in-flight" };

export function decideVendorPaymentAttempt(
  attempt: VendorPaymentAttempt,
  billId: number,
  payload: VendorPaymentPayload,
  fingerprint: string,
  generateKey: () => string,
): VendorPaymentSubmitDecision {
  if (attempt.state === "in-flight") {
    return { kind: "blocked-in-flight" };
  }

  if (attempt.state === "ambiguous") {
    if (attempt.billId !== billId) {
      return { kind: "blocked-mismatch" };
    }
    if (attempt.submittedFingerprint !== fingerprint) {
      return { kind: "blocked-mismatch" };
    }
    return {
      kind: "start",
      idempotencyKey: attempt.idempotencyKey as string,
      attempt: { ...attempt, state: "in-flight" },
    };
  }

  const idempotencyKey = generateKey();
  return {
    kind: "start",
    idempotencyKey,
    attempt: {
      state: "in-flight",
      billId,
      idempotencyKey,
      submittedFingerprint: fingerprint,
      submittedPayload: payload,
    },
  };
}

export function resolveVendorPaymentSuccess(): VendorPaymentAttempt {
  return IDLE_VENDOR_PAYMENT_ATTEMPT;
}

export function resolveVendorPaymentDefiniteRejection(): VendorPaymentAttempt {
  return IDLE_VENDOR_PAYMENT_ATTEMPT;
}

export function resolveVendorPaymentAmbiguousFailure(
  attempt: VendorPaymentAttempt,
): VendorPaymentAttempt {
  return { ...attempt, state: "ambiguous" };
}

export function shouldRestoreAmbiguousAttempt(
  attempt: VendorPaymentAttempt,
  billId: number,
): boolean {
  return attempt.state === "ambiguous" && attempt.billId === billId;
}

export type VendorPaymentFailureClass =
  | "definite-rejection"
  | "closed-period"
  | "idempotency-conflict"
  | "voided"
  | "unavailable"
  | "ambiguous";

export function classifyVendorPaymentFailure(
  err: any,
  isClosedPeriod: (err: any) => boolean,
): VendorPaymentFailureClass {
  const status = err?.response?.status;

  if (typeof status !== "number") {
    return "ambiguous";
  }

  if (status === 409) {
    if (isClosedPeriod(err)) return "closed-period";
    const detail = err?.response?.data?.detail;
    if (detail === "This has been voided. Payments can't be added.") return "voided";
    if (detail === "This payment is no longer available.") return "unavailable";
    if (detail === "idempotency_key was already used to record a different vendor payment") return "idempotency-conflict";
    return "definite-rejection";
  }

  if (status >= 400 && status < 500) {
    return "definite-rejection";
  }

  return "ambiguous";
}

export const VENDOR_PAYMENT_EDITED_RETRY_BLOCKED_MESSAGE_PURCHASE =
  "This payment may already have been recorded under your last attempt. " +
  "Restore the original date, account, and amount to retry safely, " +
  "or check this purchase's payment history below before starting a new payment.";

export const VENDOR_PAYMENT_EDITED_RETRY_BLOCKED_MESSAGE_EXPENSE =
  "This payment may already have been recorded under your last attempt. " +
  "Restore the original date, account, and amount to retry safely, " +
  "or check this expense's payment history before starting a new payment.";

export const VENDOR_PAYMENT_CONFLICT_MESSAGE_PURCHASE =
  "This payment attempt could not be completed because its idempotency key " +
  "was already used for a different request. Please try recording the " +
  "payment again.";

export const VENDOR_PAYMENT_CONFLICT_MESSAGE_EXPENSE =
  VENDOR_PAYMENT_CONFLICT_MESSAGE_PURCHASE;
